/**
 * Meilisearch-powered collection page filtering.
 * Progressive enhancement: native Shopify facets + products load normally.
 * This JS hides native facets and adds Meilisearch-powered filter/sort UI.
 * Products are only replaced when the user applies a filter or sort.
 * If anything fails, native Shopify remains untouched.
 */
const MEILI_CF_TRANSLATIONS = {
  en: {
    relevance: 'Relevance', priceLowHigh: 'Price: Low to High', priceHighLow: 'Price: High to Low',
    titleAZ: 'Title: A-Z', titleZA: 'Title: Z-A', newestFirst: 'Newest First',
    productCount: '{count} product', productCountPlural: '{count} products',
    filter: 'Filter', filters: 'Filters', clearAll: 'Clear all',
    min: 'Min', max: 'Max', noProducts: 'No products found matching your filters.'
  },
  da: {
    relevance: 'Relevans', priceLowHigh: 'Pris: Lav til høj', priceHighLow: 'Pris: Høj til lav',
    titleAZ: 'Titel: A-Å', titleZA: 'Titel: Å-A', newestFirst: 'Nyeste først',
    productCount: '{count} produkt', productCountPlural: '{count} produkter',
    filter: 'Filter', filters: 'Filtre', clearAll: 'Ryd alle',
    min: 'Min', max: 'Maks', noProducts: 'Ingen produkter fundet der matcher dine filtre.'
  },
  de: {
    relevance: 'Relevanz', priceLowHigh: 'Preis: Niedrig bis Hoch', priceHighLow: 'Preis: Hoch bis Niedrig',
    titleAZ: 'Titel: A-Z', titleZA: 'Titel: Z-A', newestFirst: 'Neueste zuerst',
    productCount: '{count} Produkt', productCountPlural: '{count} Produkte',
    filter: 'Filter', filters: 'Filter', clearAll: 'Alle löschen',
    min: 'Min', max: 'Max', noProducts: 'Keine Produkte gefunden, die Ihren Filtern entsprechen.'
  },
  fi: {
    relevance: 'Osuvuus', priceLowHigh: 'Hinta: Matalasta korkeaan', priceHighLow: 'Hinta: Korkeasta matalaan',
    titleAZ: 'Nimi: A-Ö', titleZA: 'Nimi: Ö-A', newestFirst: 'Uusimmat ensin',
    productCount: '{count} tuote', productCountPlural: '{count} tuotetta',
    filter: 'Suodatin', filters: 'Suodattimet', clearAll: 'Tyhjennä kaikki',
    min: 'Min', max: 'Max', noProducts: 'Tuotteita ei löytynyt suodattimillasi.'
  },
  no: {
    relevance: 'Relevans', priceLowHigh: 'Pris: Lav til høy', priceHighLow: 'Pris: Høy til lav',
    titleAZ: 'Tittel: A-Å', titleZA: 'Tittel: Å-A', newestFirst: 'Nyeste først',
    productCount: '{count} produkt', productCountPlural: '{count} produkter',
    filter: 'Filter', filters: 'Filtre', clearAll: 'Fjern alle',
    min: 'Min', max: 'Maks', noProducts: 'Ingen produkter funnet som matcher filtrene dine.'
  },
  sv: {
    relevance: 'Relevans', priceLowHigh: 'Pris: Lågt till högt', priceHighLow: 'Pris: Högt till lågt',
    titleAZ: 'Titel: A-Ö', titleZA: 'Titel: Ö-A', newestFirst: 'Senaste först',
    productCount: '{count} produkt', productCountPlural: '{count} produkter',
    filter: 'Filter', filters: 'Filter', clearAll: 'Rensa alla',
    min: 'Min', max: 'Max', noProducts: 'Inga produkter hittades som matchar dina filter.'
  }
};

class MeilisearchCollectionFilter {
  constructor(container, options) {
    this.container = container;
    this.meiliHost = options.meiliHost;
    this.meiliKey = options.meiliKey;
    this.dashboardUrl = options.dashboardUrl;
    this.locale = (options.locale || 'da').toLowerCase();
    this.localeRoot = options.localeRoot || '/';
    if (!this.localeRoot.endsWith('/')) this.localeRoot += '/';
    this.collectionHandle = options.collectionHandle;
    this.rowsPerPage = options.rowsPerPage || '3';
    this.moneyFormat = options.moneyFormat || '{{amount}} kr.';
    this.translations = options.translations || { buyHere: 'Buy here', soldOut: 'Sold out' };

    // State
    this.cfConfig = null;
    this.searchConfig = null;
    this.colorSwatches = {};
    this.excludedProductIds = [];
    this.activeFilters = {};
    this.priceRange = { min: null, max: null };
    this.priceBounds = { min: 0, max: 10000 };
    this.currentSort = '';
    this.currentPage = 1;
    this.hitsPerPage = 24;
    this.totalHits = 0;
    this.facetDistribution = {};
    this.facetStats = {};
    this.isSearchActive = false;

    // DOM references (native elements)
    this.nativeFacetsHeader = null;
    this.nativeGrid = null;
    this.nativePagination = null;

    // DOM references (our elements, created by JS)
    this.wrapperEl = null;
    this.sortBarEl = null;
    this.filterBarEl = null;
    this.meiliGridEl = null;
    this.paginationEl = null;
    this.mobileOverlay = null;

    this.init();
  }

  async init() {
    try {
      await this.loadConfig();
      if (!this.cfConfig || !this.cfConfig.enabled) return;

      // Capture native DOM elements
      this.nativeFacetsHeader = this.container.querySelector('#native-facets-header');
      this.nativeGrid = this.container.querySelector('#ProductGridContainer');
      this.nativePagination = this.container.querySelector('#native-pagination');
      if (!this.nativeGrid) return;

      // Read URL state
      this.readURLState();
      const hasUrlState = this.hasActiveFilters();

      // Inject our UI elements into the DOM
      this.injectUI();

      // Load facet data from Meilisearch
      try {
        await this.loadFacets();
      } catch (e) {
        console.warn('[MeiliCollection] Facet load failed, keeping native UI:', e.message);
        this.restoreNative();
        return;
      }

      // When no URL filters, count native products for the sort bar
      if (!hasUrlState) {
        var nativeCards = this.nativeGrid.querySelectorAll('#product-grid > li');
        this.totalHits = nativeCards.length;
      }

      // Render filter + sort UI
      this.renderSortBar();
      this.renderFilters();

      // Sort bar + filters are now inside #native-facets-header

      // If URL has filters/sort, immediately switch to Meilisearch results
      if (hasUrlState) {
        await this.search();
      }

      // Browser back/forward
      window.addEventListener('popstate', () => {
        this.readURLState();
        if (this.hasActiveFilters()) {
          this.search();
        } else {
          this.showNative();
          this.renderSortBar();
          this.renderFilters();
        }
      });
    } catch (e) {
      console.error('[MeiliCollection] Init failed, keeping native UI:', e.message);
      this.restoreNative();
    }
  }

  hasActiveFilters() {
    return Object.keys(this.activeFilters).length > 0 ||
           this.priceRange.min !== null ||
           this.priceRange.max !== null ||
           this.currentSort !== '';
  }

  // ---- Config Loading ----
  async loadConfig() {
    if (!this.dashboardUrl) return;
    const cacheKey = 'meili_search_config';
    let loaded = false;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < 5 * 60 * 1000) {
          this.cfConfig = data.collectionFilters || null;
          this.searchConfig = data.config || null;
          this.colorSwatches = data.colorSwatches || {};
          this.excludedProductIds = data.excludedProductIds || [];
          loaded = true;
        }
      }
    } catch (e) { /* ignore */ }

    if (!loaded) {
      const resp = await fetch(`${this.dashboardUrl}/api/meilisearch/search-config`);
      if (!resp.ok) throw new Error('Config fetch failed');
      const data = await resp.json();
      this.cfConfig = data.collectionFilters || null;
      this.searchConfig = data.config || null;
      this.colorSwatches = data.colorSwatches || {};
      this.excludedProductIds = data.excludedProductIds || [];
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ data, ts: Date.now() })); } catch (e) { /* ignore */ }
    }
  }

  // ---- URL State ----
  readURLState() {
    const params = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(params.get('page')) || 1;
    this.currentSort = params.get('sort') || '';
    this.activeFilters = {};
    this.priceRange = { min: null, max: null };
    for (const [key, val] of params.entries()) {
      if (key.startsWith('filter.')) {
        const attr = key.replace('filter.', '');
        if (attr === 'price.min') { this.priceRange.min = parseFloat(val); continue; }
        if (attr === 'price.max') { this.priceRange.max = parseFloat(val); continue; }
        if (!this.activeFilters[attr]) this.activeFilters[attr] = [];
        this.activeFilters[attr].push(val);
      }
    }
  }

  writeURLState() {
    const params = new URLSearchParams();
    if (this.currentPage > 1) params.set('page', this.currentPage);
    if (this.currentSort) params.set('sort', this.currentSort);
    for (const [attr, values] of Object.entries(this.activeFilters)) {
      for (const v of values) params.append('filter.' + attr, v);
    }
    if (this.priceRange.min !== null) params.set('filter.price.min', this.priceRange.min);
    if (this.priceRange.max !== null) params.set('filter.price.max', this.priceRange.max);
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? '?' + qs : '');
    history.pushState(null, '', newUrl);
  }

  // ---- Inject UI (create our elements, insert into DOM) ----
  injectUI() {
    const layout = this.cfConfig.layout || 'topbar';

    // Use native facets header as container for sort/filter bars
    if (this.nativeFacetsHeader) {
      // Remove skeleton loader
      var skeleton = this.nativeFacetsHeader.querySelector('.meili-skeleton');
      if (skeleton) skeleton.remove();

      this.wrapperEl = this.nativeFacetsHeader;
    } else {
      this.wrapperEl = document.createElement('div');
      this.wrapperEl.id = 'meili-cf-wrapper';
      this.nativeGrid.parentNode.insertBefore(this.wrapperEl, this.nativeGrid);
    }

    // Sort bar
    this.sortBarEl = document.createElement('div');
    this.sortBarEl.className = 'meili-cf-sort-bar flex items-center justify-between py-0';
    this.wrapperEl.appendChild(this.sortBarEl);

    // Filter bar (topbar layout — horizontal above products)
    this.filterBarEl = document.createElement('div');
    this.filterBarEl.className = 'meili-cf-filter-bar flex flex-wrap gap-3 pb-4';
    this.wrapperEl.appendChild(this.filterBarEl);

    // Meili product grid (hidden by default — only shown when search is active)
    this.meiliGridEl = document.createElement('div');
    this.meiliGridEl.id = 'meili-cf-products';
    this.meiliGridEl.style.display = 'none';

    // Pagination
    this.paginationEl = document.createElement('div');
    this.paginationEl.className = 'meili-cf-pagination flex justify-center gap-2 py-8 pb-20';
    this.paginationEl.style.display = 'none';

    // Insert grid + pagination before the native product grid
    this.nativeGrid.parentNode.insertBefore(this.meiliGridEl, this.nativeGrid);
    this.nativeGrid.parentNode.insertBefore(this.paginationEl, this.nativeGrid);

    // Inject styles
    this.injectStyles();
  }

  injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .meili-cf-facet-group { transition: all 0.15s ease; }
      .meili-cf-facet-group:hover { border-color: var(--color-border, #e5e7eb); }

      /* Mobile filter overlay */
      @media (max-width: 1023px) {
        .meili-cf-mobile-overlay {
          display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          z-index: 50; background: rgba(0,0,0,0.3);
        }
        .meili-cf-mobile-overlay.open { display: block; }
        .meili-cf-mobile-panel {
          position: fixed; top: 0; right: 0; bottom: 0; width: 85%; max-width: 380px;
          background: var(--color-secondary-bg, white); overflow-y: auto; padding: 1.25rem;
          transform: translateX(100%); transition: transform 0.3s ease;
          z-index: 51;
        }
        .meili-cf-mobile-overlay.open .meili-cf-mobile-panel { transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
  }

  // ---- Show/Hide Native vs Meilisearch ----
  showNative() {
    this.isSearchActive = false;
    this.nativeGrid.style.display = '';
    this.meiliGridEl.style.display = 'none';
    if (this.nativePagination) this.nativePagination.style.display = '';
    this.paginationEl.style.display = 'none';
    // Count native products for sort bar
    const nativeCards = this.nativeGrid.querySelectorAll('#product-grid > li');
    this.totalHits = nativeCards.length;
  }

  showMeili() {
    this.isSearchActive = true;
    this.nativeGrid.style.display = 'none';
    this.meiliGridEl.style.display = '';
    if (this.nativePagination) this.nativePagination.style.display = 'none';
    this.paginationEl.style.display = '';
  }

  restoreNative() {
    // Full restore: show native, remove our injected elements
    if (this.nativeFacetsHeader) {
      // Clear sort/filter bars from header but keep the header visible
      if (this.sortBarEl) this.sortBarEl.remove();
      if (this.filterBarEl) this.filterBarEl.remove();
    }
    if (this.nativeGrid) this.nativeGrid.style.display = '';
    if (this.nativePagination) this.nativePagination.style.display = '';
    if (this.meiliGridEl) this.meiliGridEl.remove();
    if (this.paginationEl) this.paginationEl.remove();
    if (this.mobileOverlay) this.mobileOverlay.remove();
  }

  // ---- Load Facets (no product rendering) ----
  async loadFacets() {
    const data = await this._doSearch();
    // Just use facet data, don't render products
  }

  // ---- Search (user triggered) ----
  async search() {
    try {
      const data = await this._doSearch();
      this.renderProducts(data.hits || []);
      this.showMeili();
      this.renderFilters();
      this.renderSortBar();
      this.renderPagination();
    } catch (e) {
      console.error('[MeiliCollection] Search error:', e);
      // On error, show native
      this.showNative();
      this.renderSortBar();
    }
  }

  async _doSearch() {
    const filter = this.buildFilterString();
    const sort = this.getSortArray();
    const facets = this.getEnabledFacetAttributes();
    const searchBody = {
      q: '',
      filter,
      facets,
      limit: this.hitsPerPage,
      offset: (this.currentPage - 1) * this.hitsPerPage,
      attributesToRetrieve: ['id', 'title', 'handle', 'image', 'price', 'compare_at_price', 'vendor', 'product_type', 'url', 'available', 'tags', 'color']
    };
    if (sort.length > 0) searchBody.sort = sort;

    const resp = await fetch(`${this.meiliHost}/indexes/products/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.meiliKey}` },
      body: JSON.stringify(searchBody)
    });

    if (!resp.ok) {
      const errBody = await resp.text().catch(() => '');
      console.error('[MeiliCollection] Meilisearch error:', errBody);
      throw new Error('Meilisearch query failed: ' + resp.status);
    }
    const data = await resp.json();

    this.totalHits = data.estimatedTotalHits || data.totalHits || 0;
    this.facetDistribution = data.facetDistribution || {};
    this.facetStats = data.facetStats || {};

    if (this.facetStats.price && this.priceRange.min === null && this.priceRange.max === null) {
      this.priceBounds = { min: Math.floor(this.facetStats.price.min || 0), max: Math.ceil(this.facetStats.price.max || 10000) };
    }

    return data;
  }

  buildFilterString() {
    const parts = [`collections = "${this.collectionHandle}"`, 'resource_type = "product"'];
    if (this.excludedProductIds.length > 0) {
      parts.push(`id NOT IN [${this.excludedProductIds.join(', ')}]`);
    }
    for (const [attr, values] of Object.entries(this.activeFilters)) {
      if (values.length === 0) continue;
      if (attr === 'available') {
        parts.push(values.map(v => `available = ${v}`).join(' OR '));
      } else {
        const conditions = values.map(v => `${attr} = "${v.replace(/"/g, '\\"')}"`);
        parts.push(conditions.length === 1 ? conditions[0] : `(${conditions.join(' OR ')})`);
      }
    }
    if (this.priceRange.min !== null) parts.push(`price >= ${this.priceRange.min}`);
    if (this.priceRange.max !== null) parts.push(`price <= ${this.priceRange.max}`);
    return parts.join(' AND ');
  }

  getSortArray() {
    switch (this.currentSort) {
      case 'price-asc': return ['price:asc'];
      case 'price-desc': return ['price:desc'];
      case 'title-asc': return ['title:asc'];
      case 'title-desc': return ['title:desc'];
      case 'created-desc': return ['created_at:desc'];
      default: return [];
    }
  }

  getEnabledFacetAttributes() {
    if (!this.cfConfig || !this.cfConfig.facets) return [];
    return this.cfConfig.facets.filter(f => f.enabled).map(f => f.attribute);
  }

  // ---- Render Products ----
  renderProducts(hits) {
    if (!hits.length) {
      this.meiliGridEl.innerHTML = '<div class="text-center py-16 text-gray-500">' + this.esc(this.t('noProducts')) + '</div>';
      return;
    }

    const colClass = this.rowsPerPage === '4' ? 'col-span-6 md:col-span-3' : 'col-span-12 md:col-span-6 xlg:col-span-4';

    let html = '<div class="collection flex flex-col"><ul class="page-width product-grid gap-x-6 gap-y-12 grid grid-cols-12 w-full h-full pb-6">';
    hits.forEach(hit => {
      html += `<li data-product-id="${hit.id}" class="grid__item ${colClass}">${this.buildCard(hit)}</li>`;
    });
    html += '</ul></div>';

    this.meiliGridEl.innerHTML = html;
  }

  buildCard(hit) {
    const price = hit.price ? this.formatMoney(hit.price) : '';
    const comparePrice = hit.compare_at_price && hit.compare_at_price > hit.price ? this.formatMoney(hit.compare_at_price) : '';
    const href = hit.handle ? `${this.localeRoot}products/${this.esc(hit.handle)}` : '#';
    const isOnSale = !!comparePrice;
    const isAvailable = hit.available !== false;
    const titleParts = (hit.title || 'Untitled').split('-');
    const titleMain = titleParts[0].trim();
    const titleSub = titleParts.length > 1 ? titleParts.slice(1).join('-').trim() : '';
    const imgSrc = hit.image ? this.getResizedImage(hit.image, 832) : '';

    return `
      <div class="product_card group rounded-xl overflow-hidden bg-secondary-bg flex flex-col h-fit cursor-pointer relative justify-start w-full">
        <div class="${isAvailable ? '' : 'opacity-60 '}image_container rounded-t-xl bg-secondary-bg-400 relative group overflow-hidden z-3">
          <a class="group flex items-center justify-center w-full bg-secondary-bg-400 h-[275px] xlg:h-[355px] pb-2" href="${href}">
            ${imgSrc
              ? `<div class="card-media-wrapper h-fit w-full group-hover:scale-105 transition-transform duration-300"><img src="${this.esc(imgSrc)}" alt="${this.esc(hit.title)}" class="w-full h-fit object-contain" loading="lazy"></div>`
              : '<div class="w-full h-full flex items-center justify-center text-gray-300"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg></div>'}
          </a>
        </div>
        <div class="Product-info border-t cursor-default border-border pt-6 z-[2] flex flex-col pl-4 md:pl-4 pt-4 pb-2 gap-0 md:gap-1 text-left justify-between">
          <div class="flex flex-col md:pt-2 md:gap-y-1">
            <div class="flex flex-col pt-1 pb-2">
              <div class="flex flex-row justify-between items-start w-full">
                <div class="flex flex-row gap-2 w-full">
                  <a class="flex flex-col items-start mb-3 w-fit relative" href="${href}">
                    <div class="flex gap-x-1 items-center text-xl md:text-xl Heading text-primary-black mb-1">
                      <span class="font-bold">${this.esc(titleMain)}</span>
                      ${titleSub ? `<span class="font-light text-sm">- ${this.esc(titleSub)}</span>` : ''}
                    </div>
                    ${hit.vendor ? `<span class="text-sm font-normal h-[13px] mt-2 mb-1">${this.esc(hit.vendor)}</span>` : '<span class="text-sm font-normal h-[13px] mt-2 mb-1"></span>'}
                  </a>
                </div>
                <div class="pricing w-fit mb-2 pr-4 md:mb-2 flex gap-2">
                  <div class="Price text-primary-black w-fit flex flex-wrap gap-x-3 flex-row justify-end">
                    <div class="flex flex-col items-end justify-end gap-1 w-max">
                      ${isOnSale
                        ? `<span class="text-base text-wrap md:text-lg font-bold Heading !text-lg md:!text-xl text-red-500">${price}</span>
                           <p class="Price text-sm sm:text-base text-wrap line-through">${comparePrice}</p>`
                        : `<b class="text-base md:text-lg text-wrap font-bold Heading">${price}</b>`}
                    </div>
                  </div>
                </div>
              </div>
              ${isAvailable
                ? `<a href="${href}" class="flex items-center mt-1 mb-3 text-center justify-center gap-x-2 rounded-lg px-4 min-h-[35px] text-base font-semibold text-white w-fit min-w-[150px] bg-primary-black hover:bg-medium-green">${this.esc(this.translations.buyHere)}</a>`
                : `<button disabled class="flex gap-x-2 items-center text-base w-fit py-2 rounded-lg text-primary-text border border-border mb-3 mt-1 min-h-[35px] px-4 opacity-60">${this.esc(this.translations.soldOut)}</button>`}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  getResizedImage(url, width) {
    if (!url) return '';
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d*|\d+x\d*)?(\.(jpg|jpeg|png|gif|webp))/, `_${width}x$2`);
    }
    return url;
  }

  // ---- Render Sort Bar ----
  renderSortBar() {
    const so = this.cfConfig.sortOptions || {};
    const options = [];
    if (so.relevance !== false) options.push({ value: '', label: this.t('relevance') });
    if (so.priceAsc !== false) options.push({ value: 'price-asc', label: this.t('priceLowHigh') });
    if (so.priceDesc !== false) options.push({ value: 'price-desc', label: this.t('priceHighLow') });
    if (so.titleAsc !== false) options.push({ value: 'title-asc', label: this.t('titleAZ') });
    if (so.titleDesc !== false) options.push({ value: 'title-desc', label: this.t('titleZA') });
    if (so.createdDesc !== false) options.push({ value: 'created-desc', label: this.t('newestFirst') });

    const optionsHtml = options.map(o =>
      `<option value="${o.value}" ${this.currentSort === o.value ? 'selected' : ''}>${o.label}</option>`
    ).join('');

    this.sortBarEl.innerHTML = `
      <div class="flex items-center gap-3">
        <span class="text-sm opacity-60">${this.t(this.totalHits !== 1 ? 'productCountPlural' : 'productCount').replace('{count}', this.totalHits)}</span>
      </div>
      <div class="flex items-center gap-2">
        <button class="meili-cf-mobile-filter-btn lg:hidden flex items-center gap-1.5 text-sm font-medium border border-border rounded-xl px-3 py-2 hover:opacity-80 bg-secondary-bg">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
          ${this.esc(this.t('filter'))}
        </button>
        <select id="meili-cf-sort" class="text-sm border border-border rounded-xl px-3 py-2 bg-secondary-bg text-primary-text cursor-pointer">
          ${optionsHtml}
        </select>
      </div>
    `;

    // Sort listener
    const sortEl = this.sortBarEl.querySelector('#meili-cf-sort');
    if (sortEl) {
      sortEl.addEventListener('change', () => {
        this.currentSort = sortEl.value;
        this.currentPage = 1;
        this.writeURLState();
        this.search();
      });
    }

    // Mobile filter button
    const mobileBtn = this.sortBarEl.querySelector('.meili-cf-mobile-filter-btn');
    if (mobileBtn) {
      mobileBtn.addEventListener('click', () => this.openMobileFilters());
    }
  }

  // ---- Render Filters ----
  renderFilters() {
    if (!this.cfConfig || !this.cfConfig.facets) return;
    const enabledFacets = this.cfConfig.facets.filter(f => f.enabled);
    if (enabledFacets.length === 0) { this.filterBarEl.innerHTML = ''; return; }

    let html = this.buildActiveFilterChips();

    for (const facet of enabledFacets) {
      const dist = this.facetDistribution[facet.attribute] || {};
      if (facet.attribute === 'price') {
        html += this.buildPriceFilter(facet);
      } else if (facet.type === 'color' && this.colorSwatches) {
        html += this.buildColorFilter(facet, dist);
      } else {
        html += this.buildCheckboxFilter(facet, dist);
      }
    }

    // Desktop: horizontal filter bar
    this.filterBarEl.innerHTML = html;
    this.attachFilterListeners(this.filterBarEl);

    // Also update mobile panel if it exists
    this.updateMobileFilters(enabledFacets);
  }

  buildActiveFilterChips() {
    const chips = [];
    for (const [attr, values] of Object.entries(this.activeFilters)) {
      const facetCfg = this.cfConfig.facets.find(f => f.attribute === attr);
      const label = facetCfg ? facetCfg.label : attr;
      for (const v of values) {
        chips.push(`<button class="meili-cf-chip inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-secondary-bg border border-border hover:bg-gray-200 rounded-full transition-colors" data-attr="${this.esc(attr)}" data-value="${this.esc(v)}">
          ${this.esc(label)}: ${this.esc(v)}
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>`);
      }
    }
    if (this.priceRange.min !== null || this.priceRange.max !== null) {
      const label = this.cfConfig.facets.find(f => f.attribute === 'price')?.label || 'Price';
      chips.push(`<button class="meili-cf-chip inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-secondary-bg border border-border hover:bg-gray-200 rounded-full transition-colors" data-attr="price" data-value="range">
        ${this.esc(label)}: ${this.priceRange.min ?? this.priceBounds.min} - ${this.priceRange.max ?? this.priceBounds.max}
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`);
    }
    if (chips.length === 0) return '';
    return `<div class="meili-cf-active-filters w-full flex flex-wrap gap-2 items-center pb-2">
      ${chips.join('')}
      <button class="meili-cf-clear-all text-xs text-primary-text hover:opacity-70 font-medium underline">${this.esc(this.t('clearAll'))}</button>
    </div>`;
  }

  buildCheckboxFilter(facet, dist) {
    const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return '';
    const activeValues = this.activeFilters[facet.attribute] || [];
    const isCollapsed = facet.collapsed && activeValues.length === 0;

    let html = `<div class="meili-cf-facet-group border border-border rounded-xl overflow-hidden bg-secondary-bg">
      <button class="meili-cf-facet-toggle w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-primary-text hover:opacity-80 cursor-pointer" data-target="meili-cf-facet-${facet.attribute}">
        ${this.esc(facet.label)}${activeValues.length ? ` <span class="text-xs opacity-60">(${activeValues.length})</span>` : ''}
        <svg class="w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-180'}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
      </button>
      <div id="meili-cf-facet-${facet.attribute}" class="meili-cf-facet-body px-4 pb-3 ${isCollapsed ? 'hidden' : ''}" style="max-height: 280px; overflow-y: auto; min-width: 200px;">`;

    for (const [value, count] of entries) {
      const checked = activeValues.includes(value) ? 'checked' : '';
      html += `<label class="flex items-center gap-2 py-1.5 cursor-pointer hover:opacity-80 rounded px-1 text-sm">
        <input type="checkbox" class="meili-cf-filter-cb rounded" data-attr="${this.esc(facet.attribute)}" data-value="${this.esc(value)}" ${checked}>
        <span class="flex-1 truncate text-primary-text">${this.esc(value)}</span>
        <span class="text-xs opacity-50">${count}</span>
      </label>`;
    }

    html += '</div></div>';
    return html;
  }

  buildColorFilter(facet, dist) {
    const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return '';
    const activeValues = this.activeFilters[facet.attribute] || [];
    const isCollapsed = facet.collapsed && activeValues.length === 0;

    let html = `<div class="meili-cf-facet-group border border-border rounded-xl overflow-hidden bg-secondary-bg">
      <button class="meili-cf-facet-toggle w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-primary-text hover:opacity-80 cursor-pointer" data-target="meili-cf-facet-${facet.attribute}">
        ${this.esc(facet.label)}${activeValues.length ? ` <span class="text-xs opacity-60">(${activeValues.length})</span>` : ''}
        <svg class="w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-180'}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
      </button>
      <div id="meili-cf-facet-${facet.attribute}" class="meili-cf-facet-body px-4 pb-3 ${isCollapsed ? 'hidden' : ''}">
        <div class="flex flex-wrap gap-2">`;

    for (const [value, count] of entries) {
      const isActive = activeValues.includes(value);
      const swatch = this.colorSwatches[value.toLowerCase()] || {};
      const bgStyle = swatch.hex ? `background-color: ${swatch.hex}` : `background-color: ${value.toLowerCase()}`;
      html += `<button class="meili-cf-color-swatch w-8 h-8 rounded-full border-2 ${isActive ? 'border-primary-text ring-2 ring-gray-300' : 'border-border hover:border-gray-400'} transition-all cursor-pointer" data-attr="${this.esc(facet.attribute)}" data-value="${this.esc(value)}" style="${bgStyle}" title="${this.esc(value)} (${count})"></button>`;
    }

    html += '</div></div></div>';
    return html;
  }

  buildPriceFilter(facet) {
    const min = this.priceBounds.min;
    const max = this.priceBounds.max;
    const curMin = this.priceRange.min ?? min;
    const curMax = this.priceRange.max ?? max;
    const isCollapsed = facet.collapsed && this.priceRange.min === null && this.priceRange.max === null;

    return `<div class="meili-cf-facet-group border border-border rounded-xl overflow-hidden bg-secondary-bg">
      <button class="meili-cf-facet-toggle w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-primary-text hover:opacity-80 cursor-pointer" data-target="meili-cf-facet-price">
        ${this.esc(facet.label)}
        <svg class="w-4 h-4 transition-transform ${isCollapsed ? '' : 'rotate-180'}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
      </button>
      <div id="meili-cf-facet-price" class="meili-cf-facet-body px-4 pb-3 ${isCollapsed ? 'hidden' : ''}">
        <div class="flex items-center gap-3 mt-1">
          <div class="flex-1">
            <label class="text-xs opacity-60">${this.esc(this.t('min'))}</label>
            <input type="number" class="meili-cf-price-min w-full border border-border rounded-lg px-2 py-1.5 text-sm bg-white" value="${curMin}" min="${min}" max="${max}" step="1">
          </div>
          <span class="opacity-40 mt-4">&mdash;</span>
          <div class="flex-1">
            <label class="text-xs opacity-60">${this.esc(this.t('max'))}</label>
            <input type="number" class="meili-cf-price-max w-full border border-border rounded-lg px-2 py-1.5 text-sm bg-white" value="${curMax}" min="${min}" max="${max}" step="1">
          </div>
          <button class="meili-cf-price-apply mt-4 px-3 py-1.5 bg-primary-black text-white text-xs rounded-lg font-medium hover:opacity-80 transition-opacity cursor-pointer">Go</button>
        </div>
      </div>
    </div>`;
  }

  // ---- Render Pagination ----
  renderPagination() {
    const totalPages = Math.ceil(this.totalHits / this.hitsPerPage);
    if (totalPages <= 1) { this.paginationEl.innerHTML = ''; this.paginationEl.style.display = 'none'; return; }

    this.paginationEl.style.display = '';
    let html = '';
    if (this.currentPage > 1) {
      html += `<button class="meili-cf-page px-3 py-2 text-sm border border-border rounded-xl hover:opacity-80 cursor-pointer" data-page="${this.currentPage - 1}">&laquo;</button>`;
    }

    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(totalPages, this.currentPage + 2);
    if (start > 1) {
      html += `<button class="meili-cf-page px-3 py-2 text-sm border border-border rounded-xl hover:opacity-80 cursor-pointer" data-page="1">1</button>`;
      if (start > 2) html += '<span class="px-2 py-2 opacity-40">...</span>';
    }
    for (let i = start; i <= end; i++) {
      const active = i === this.currentPage ? 'bg-primary-black text-white border-primary-black' : 'border-border hover:opacity-80';
      html += `<button class="meili-cf-page px-3 py-2 text-sm border rounded-xl ${active} cursor-pointer" data-page="${i}">${i}</button>`;
    }
    if (end < totalPages) {
      if (end < totalPages - 1) html += '<span class="px-2 py-2 opacity-40">...</span>';
      html += `<button class="meili-cf-page px-3 py-2 text-sm border border-border rounded-xl hover:opacity-80 cursor-pointer" data-page="${totalPages}">${totalPages}</button>`;
    }

    if (this.currentPage < totalPages) {
      html += `<button class="meili-cf-page px-3 py-2 text-sm border border-border rounded-xl hover:opacity-80 cursor-pointer" data-page="${this.currentPage + 1}">&raquo;</button>`;
    }

    this.paginationEl.innerHTML = html;

    this.paginationEl.querySelectorAll('.meili-cf-page').forEach(btn => {
      btn.addEventListener('click', () => {
        this.currentPage = parseInt(btn.dataset.page);
        this.writeURLState();
        this.search();
        window.scrollTo({ top: this.wrapperEl.offsetTop - 80, behavior: 'smooth' });
      });
    });
  }

  // ---- Filter Event Listeners ----
  attachFilterListeners(root) {
    // Checkbox filters
    root.querySelectorAll('.meili-cf-filter-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        const attr = cb.dataset.attr;
        const value = cb.dataset.value;
        if (!this.activeFilters[attr]) this.activeFilters[attr] = [];
        if (cb.checked) {
          if (!this.activeFilters[attr].includes(value)) this.activeFilters[attr].push(value);
        } else {
          this.activeFilters[attr] = this.activeFilters[attr].filter(v => v !== value);
          if (this.activeFilters[attr].length === 0) delete this.activeFilters[attr];
        }
        this.currentPage = 1;
        this.writeURLState();
        this.search();
      });
    });

    // Color swatches
    root.querySelectorAll('.meili-cf-color-swatch').forEach(btn => {
      btn.addEventListener('click', () => {
        const attr = btn.dataset.attr;
        const value = btn.dataset.value;
        if (!this.activeFilters[attr]) this.activeFilters[attr] = [];
        const idx = this.activeFilters[attr].indexOf(value);
        if (idx === -1) {
          this.activeFilters[attr].push(value);
        } else {
          this.activeFilters[attr].splice(idx, 1);
          if (this.activeFilters[attr].length === 0) delete this.activeFilters[attr];
        }
        this.currentPage = 1;
        this.writeURLState();
        this.search();
      });
    });

    // Price apply
    const priceApply = root.querySelector('.meili-cf-price-apply');
    if (priceApply) {
      priceApply.addEventListener('click', () => {
        const minEl = root.querySelector('.meili-cf-price-min');
        const maxEl = root.querySelector('.meili-cf-price-max');
        const min = parseFloat(minEl?.value);
        const max = parseFloat(maxEl?.value);
        this.priceRange.min = !isNaN(min) && min > this.priceBounds.min ? min : null;
        this.priceRange.max = !isNaN(max) && max < this.priceBounds.max ? max : null;
        this.currentPage = 1;
        this.writeURLState();
        this.search();
      });
    }

    // Facet toggle (expand/collapse)
    root.querySelectorAll('.meili-cf-facet-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.target;
        const body = root.querySelector('#' + target) || document.getElementById(target);
        if (body) body.classList.toggle('hidden');
        const icon = btn.querySelector('svg');
        if (icon) icon.classList.toggle('rotate-180');
      });
    });

    // Active filter chips (remove)
    root.querySelectorAll('.meili-cf-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const attr = chip.dataset.attr;
        const value = chip.dataset.value;
        if (attr === 'price') {
          this.priceRange = { min: null, max: null };
        } else if (this.activeFilters[attr]) {
          this.activeFilters[attr] = this.activeFilters[attr].filter(v => v !== value);
          if (this.activeFilters[attr].length === 0) delete this.activeFilters[attr];
        }
        this.currentPage = 1;
        this.writeURLState();
        if (!this.hasActiveFilters()) {
          this.showNative();
          this.loadFacets().then(() => { this.renderFilters(); this.renderSortBar(); });
        } else {
          this.search();
        }
      });
    });

    // Clear all
    const clearAll = root.querySelector('.meili-cf-clear-all');
    if (clearAll) {
      clearAll.addEventListener('click', () => {
        this.activeFilters = {};
        this.priceRange = { min: null, max: null };
        this.currentSort = '';
        this.currentPage = 1;
        this.writeURLState();
        this.showNative();
        this.loadFacets().then(() => { this.renderFilters(); this.renderSortBar(); });
      });
    }
  }

  // ---- Mobile Filters ----
  openMobileFilters() {
    if (!this.mobileOverlay) {
      this.mobileOverlay = document.createElement('div');
      this.mobileOverlay.className = 'meili-cf-mobile-overlay';
      this.mobileOverlay.innerHTML = `
        <div class="meili-cf-mobile-panel">
          <div class="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
            <span class="text-lg font-bold">${this.esc(this.t('filters'))}</span>
            <button class="meili-cf-mobile-close p-1 hover:bg-gray-100 rounded-lg cursor-pointer">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="meili-cf-mobile-filters-content flex flex-col gap-3"></div>
        </div>
      `;
      document.body.appendChild(this.mobileOverlay);

      // Close handlers
      this.mobileOverlay.querySelector('.meili-cf-mobile-close').addEventListener('click', () => this.closeMobileFilters());
      this.mobileOverlay.addEventListener('click', (e) => {
        if (e.target === this.mobileOverlay) this.closeMobileFilters();
      });
    }

    // Populate mobile filters
    this.updateMobileFilters(this.cfConfig.facets.filter(f => f.enabled));
    this.mobileOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  closeMobileFilters() {
    if (this.mobileOverlay) {
      this.mobileOverlay.classList.remove('open');
      document.body.style.overflow = '';
    }
  }

  updateMobileFilters(enabledFacets) {
    if (!this.mobileOverlay) return;
    const content = this.mobileOverlay.querySelector('.meili-cf-mobile-filters-content');
    if (!content) return;

    let html = this.buildActiveFilterChips();
    for (const facet of enabledFacets) {
      const dist = this.facetDistribution[facet.attribute] || {};
      if (facet.attribute === 'price') {
        html += this.buildPriceFilter(facet);
      } else if (facet.type === 'color' && this.colorSwatches) {
        html += this.buildColorFilter(facet, dist);
      } else {
        html += this.buildCheckboxFilter(facet, dist);
      }
    }
    content.innerHTML = html;
    this.attachFilterListeners(content);
  }

  // ---- Utilities ----
  formatMoney(amountInCents) {
    const num = parseFloat(amountInCents);
    if (isNaN(num)) return '';
    const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    const withSeparator = this.locale === 'da' || this.locale === 'de'
      ? formatted.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      : formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, withSeparator)
                           .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(num).toString())
                           .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withSeparator);
  }

  t(key) {
    const lang = MEILI_CF_TRANSLATIONS[this.locale] || MEILI_CF_TRANSLATIONS.en;
    return lang[key] || MEILI_CF_TRANSLATIONS.en[key] || key;
  }

  esc(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
